import { createClient } from '@supabase/supabase-js'
import { SUPABASE_URL, SUPABASE_KEY, HAS_BACKEND, MEDIA_BASE } from './config.js'
import { SAMPLE_STORIES } from './sample-data.js'

/* Reads go straight to Supabase against the `stories_public` view, which RLS
   already restricts to visible rows with contact details stripped. Writes do
   NOT go to Supabase — they go to Cloudflare Pages Functions that hold the
   service_role key server-side. See supabase/schema.sql for why. */

export const supabase = HAS_BACKEND
  ? createClient(SUPABASE_URL, SUPABASE_KEY, { auth: { persistSession: false } })
  : null

/** True when the map is showing the bundled invented records rather than a real
    corpus. The UI must say so out loud. */
export const usingSampleData = !HAS_BACKEND

export async function fetchStories() {
  if (!HAS_BACKEND) return SAMPLE_STORIES

  const { data, error } = await supabase
    .from('stories_public')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(5000)

  if (error) throw new Error(`Could not load stories: ${error.message}`)
  return data ?? []
}

/** Resolve a Storage key to a URL. Rows hold bucket keys, not URLs, so the
    project can change buckets without a data migration. */
export const mediaUrl = (path) =>
  !path ? null : /^https?:/.test(path) ? path : MEDIA_BASE + path

/* ── Writes ──────────────────────────────────────────────────────────────── */

async function post(endpoint, body) {
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })

  let payload = null
  try { payload = await res.json() } catch { /* empty or non-JSON body */ }

  if (!res.ok) {
    const err = new Error(payload?.error || `HTTP ${res.status}`)
    err.status = res.status
    err.code = payload?.code
    throw err
  }
  return payload
}

export const submitStory = (story) => post('/api/submit', story)
export const reportStory = (story_id, reason, detail) =>
  post('/api/report', { story_id, reason, detail })

/**
 * Upload one file. The browser is never trusted with a Storage write key:
 * /api/upload-url mints a single-use signed upload URL server-side, and we PUT
 * to that. Returns the bucket key to store on the row.
 */
export async function uploadFile(file, kind) {
  if (!HAS_BACKEND) throw new Error('No backend configured')

  const { path, token } = await post('/api/upload-url', {
    filename: file.name,
    contentType: file.type,
    size: file.size,
    kind,
  })

  const { error } = await supabase.storage
    .from('media')
    .uploadToSignedUrl(path, token, file)

  if (error) throw new Error(`Upload failed: ${error.message}`)
  return path
}

/* ── Export ──────────────────────────────────────────────────────────────── */

/** GeoJSON export. Relocations become LineStrings so the file opens usefully in
    QGIS; everything else is a Point. This is the format the research actually
    needs downstream, so it is a first-class feature rather than a nicety. */
export function toGeoJSON(stories) {
  return {
    type: 'FeatureCollection',
    features: stories.map((s) => {
      const isJourney = s.kind === 'relocation' && s.dest_lng != null
      return {
        type: 'Feature',
        id: s.id,
        geometry: isJourney
          ? {
              type: 'LineString',
              coordinates: [[s.origin_lng, s.origin_lat], [s.dest_lng, s.dest_lat]],
            }
          : { type: 'Point', coordinates: [s.origin_lng, s.origin_lat] },
        properties: {
          id: s.id,
          kind: s.kind,
          title_zh: s.title_zh, title_en: s.title_en,
          narrative_zh: s.narrative_zh, narrative_en: s.narrative_en,
          communities: (s.communities || []).join('; '),
          year_start: s.year_start, year_end: s.year_end,
          place_zh: s.place_zh, place_en: s.place_en,
          dest_place_zh: s.dest_place_zh, dest_place_en: s.dest_place_en,
          precision_level: s.precision_level,
          displacement_km: s.displacement_km,
          species: (s.species || []).join('; '),
          ecology_change: s.ecology_change,
          narrator_label: s.narrator_label,
          source_note: s.source_note,
          license: s.license,
        },
      }
    }),
  }
}

export function toCSV(stories) {
  const cols = [
    'id', 'kind', 'title_zh', 'title_en', 'narrative_zh', 'narrative_en',
    'communities', 'year_start', 'year_end',
    'origin_lat', 'origin_lng', 'place_zh', 'place_en',
    'dest_lat', 'dest_lng', 'dest_place_zh', 'dest_place_en',
    'precision_level', 'displacement_km', 'species', 'ecology_change',
    'narrator_label', 'narrator_birth_year', 'source_note', 'license',
  ]

  const cell = (v) => {
    if (v == null) return ''
    const s = Array.isArray(v) ? v.join('; ') : String(v)
    return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s
  }

  // Excel on Windows needs the BOM to read UTF-8 Chinese correctly, and this
  // corpus is mostly Chinese.
  return '﻿' + [
    cols.join(','),
    ...stories.map((s) => cols.map((c) => cell(s[c])).join(',')),
  ].join('\n')
}

export function download(filename, text, mime) {
  const url = URL.createObjectURL(new Blob([text], { type: mime }))
  const a = Object.assign(document.createElement('a'), { href: url, download: filename })
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
